import type { LucideIcon } from "lucide-react";
import {
  Archive, BadgeDollarSign, BarChart3, Blocks, Building2, Clock, Crown, FileSignature, FileText, Files, FolderOpen, Gem, GitBranch,
  HardDrive, LayoutTemplate, ListChecks, PenTool, Plug, Receipt, Shield, Sparkles, Star, UserCog, Users,
} from "lucide-react";
import type { TFunction } from "i18next";

/** Icon per meter key; unknown keys fall back to a neutral glyph. */
export const METER_ICONS: Record<string, LucideIcon> = {
  users: Users,
  admin_users: UserCog,
  active_contracts: FileText,
  legacy_contracts: Archive,
  templates: LayoutTemplate,
  ai_credits: Sparkles,
  bulk_documents: Files,
  storage_gb: HardDrive,
  esign_envelopes: PenTool,
  obligation_records: ListChecks,
  receivable_records: Receipt,
  workflows: GitBranch,
  api_calls: Plug,
  integrations: Blocks,
};

export const MODULE_ICONS: Record<string, LucideIcon> = {
  contract_studio: FileSignature,
  contract_repository: FolderOpen,
  obligation_hub: ListChecks,
  receivables_engine: BadgeDollarSign,
  workflow: GitBranch,
  esign: PenTool,
  ai: Sparkles,
  analytics: BarChart3,
  api: Plug,
  integrations: Blocks,
};

export const PLAN_ICONS: Record<string, LucideIcon> = {
  PLAN_TRIAL: Clock,
  PLAN_SILVER: Shield,
  PLAN_GOLD: Crown,
  PLAN_PLATINUM: Gem,
  PLAN_ENTERPRISE: Building2,
};

export const planIcon = (code?: string | null): LucideIcon => (code && PLAN_ICONS[code]) || Star;
export const meterIcon = (key: string): LucideIcon => METER_ICONS[key] || FileText;
export const moduleIcon = (key: string): LucideIcon => MODULE_ICONS[key] || Blocks;

/**
 * Catalogue names come from the database in English. These helpers look
 * up a translation by stable key and fall back to the stored name, so a
 * meter or plan Super Admin adds later still renders without a release.
 */
export const meterLabel = (t: TFunction, key: string, fallback?: string | null) =>
  t(`catalogue.meters.${key}`, { defaultValue: fallback || key });

export const moduleLabel = (t: TFunction, key: string, fallback?: string | null) =>
  t(`catalogue.modules.${key}`, { defaultValue: fallback || key });

export const levelLabel = (t: TFunction, level?: string | null) =>
  level ? t(`catalogue.levels.${level.toLowerCase()}`, { defaultValue: level }) : "";

export const planText = (t: TFunction, code: string | null | undefined, field: "name" | "tagline" | "audience", fallback?: string | null) =>
  code ? t(`catalogue.plans.${code}.${field}`, { defaultValue: fallback || "" }) : fallback || "";

export const featureLabel = (t: TFunction, key: string, fallback?: string | null) =>
  t(`catalogue.features.${key}`, { defaultValue: fallback || key });

/** Units are short data strings ("credits", "GB"); translated by value. */
export const unitLabel = (t: TFunction, unit?: string | null) =>
  unit ? t(`catalogue.units.${unit.toLowerCase()}`, { defaultValue: unit }) : "";

export const tierLabel = (t: TFunction, tier?: string | null) =>
  tier ? t(`catalogue.tiers.${tier.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`, { defaultValue: tier }) : "";

/**
 * Recharge packs are looked up by code when we have one; a recharge row
 * only carries the pack's name, so it falls back to a slug of that name.
 */
export const packLabel = (t: TFunction, name?: string | null, code?: string | null) => {
  if (code) { const byCode = t(`catalogue.packs.${code}`, { defaultValue: "" }); if (byCode) return byCode; }
  if (!name) return "";
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  return t(`catalogue.packNames.${slug}`, { defaultValue: name });
};
